import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import './search-input.scss';

import { INPUT_DEBOUNCE_TIME } from '../../../constants/common.constants';
import { debounce } from '../../../infrastructure/utils/debounce.utils';

import { CloseIcon } from '../icons/icons';

export enum ButtonType { Blue, Black }

interface SearchInputProps {
    withResetSearchButton: boolean;
    resetSearch?: () => void;
    value: string;
    onEnterButtonDown: (v: string) => void;
    withDebounce: boolean;
    onDebouncedCancel?: () => void;
    debounceTime?: number;
    className: string;
    buttonType: ButtonType;
    onChange: (e: React.ChangeEvent<HTMLInputElement>, v: string) => void;
    placeholder: string;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    autoFocus: boolean;
}

const SearchInput = (props: Partial<SearchInputProps>) => {
    const {
        resetSearch,
        value,
        className,
        withResetSearchButton = false,
        onChange = () => null,
        onEnterButtonDown = () => null,
        withDebounce = false,
        buttonType = ButtonType.Blue,
        autoFocus = false,
        placeholder,
        onDebouncedCancel,
        debounceTime = INPUT_DEBOUNCE_TIME,
        ...otherProps
    } = props;

    const intl = useIntl();

    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);
    const [onChangeCallback, setOnChangeCallback] = useState(null);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.persist();
        const newValue = e.currentTarget.value;
        setInputValue(newValue);
        onChangeCallback(e, newValue);

        if (newValue === '') {
            resetSearch?.();
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter') {
            onEnterButtonDown(inputValue);
        }
    };

    const handleReset = () => {
        setInputValue('');
        onChangeCallback?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>, '');
        resetSearch?.();
    };

    useEffect(() => {
        const callback = withDebounce ? debounce(onChange, debounceTime, onDebouncedCancel) : onChange;
        setOnChangeCallback(() => callback);

        setTimeout(() => {
            if (autoFocus && inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    }, []);

    useEffect(() => {
        if (!value && inputValue)
            setInputValue('');
    }, [value]);

    return (
        <>
            <input
                placeholder={
                    placeholder || intl.formatMessage({
                        id: 'oe.common.search',
                        defaultMessage: 'Search',
                        description: 'Search for some name/last name/id etc',
                    })
                }
                type='text'
                className={classnames('search-input', className, {
                    'search-input--with-reset': withResetSearchButton
                })}
                value={value || inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                ref={inputRef}
                {...otherProps}
            />
            { withResetSearchButton && (!!value?.length || !!inputValue.length) &&
               <button type='button' onClick={handleReset} className={classnames('reset-button', {
                    'reset-button--blue': buttonType === ButtonType.Blue,
                    'reset-button--black': buttonType === ButtonType.Black,
                })}>
                    <CloseIcon className='reset-button__icon' />
                </button>
            }
        </>
    );
};

export { SearchInput };
